import { useEffect } from 'react';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import {
  Building2,
  Users,
  AppWindow,
  Settings,
  LogOut,
  LayoutDashboard,
  ClipboardCheck,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/i18n';
import { LanguageToggle } from '@/components/ui/language-toggle';
import { cn } from '@/lib/utils';
import { tokenManager } from '@/api/client';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { user, isLoading, isAuthenticated, logout } = useAuth();
  const { t } = useI18n();

  const navItems = [
    { href: '/admin', label: t.admin.overview, icon: LayoutDashboard },
    { href: '/admin/users', label: t.admin.userManagement, icon: Users },
    { href: '/admin/reviews', label: t.admin.appReview, icon: ClipboardCheck },
    { href: '/admin/apps', label: t.admin.publishedAppsTitle, icon: AppWindow },
    { href: '/admin/settings', label: t.admin.settings, icon: Settings },
  ];

  useEffect(() => {
    // 如果 token 存在但 user 还未加载，等待状态更新
    if (tokenManager.isValid() && !user) {
      return;
    }
    if (!isLoading && (!isAuthenticated || user?.role !== 'admin')) {
      navigate('/myorg');
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/myorg');
  };

  // 等待加载或 token 存在但 user 还未加载
  if (isLoading || (tokenManager.isValid() && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-2 px-6 h-16 border-b border-gray-100">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-gray-900">Estha MyOrg</span>
              <p className="text-xs text-gray-500">{t.admin.title}</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive =
                item.href === '/admin'
                  ? pathname === '/admin'
                  : pathname.startsWith(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Language toggle */}
          <div className="px-4 py-3 border-t border-gray-100">
            <LanguageToggle variant="full" />
          </div>

          {/* User info */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-primary-700">
                  {user?.name.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2 mt-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              {t.common.logout}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="pl-64">
        <div className="min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
